import { FC, useState } from 'react';
import { Skeleton, Typography } from '@mui/material';
import type { CapIQTransaction } from 'api';
import { useGetCompanyCapIQFundingRounds } from 'services/hooks/companies';
import {
  RoundsTable,
  RoundsTableCell,
  RoundsTableHead,
  RoundsTableRow,
} from '../FundingRoundsCard.styles';
import { CapIQFundingRoundsCardProps } from './CapIQFundingRoundsCard.props';
import { formatCapIQAmount, formatDate } from '../FundingRoundsCard.utils';

const COLUMN_HEADERS = ['#', 'Round / Features', 'Date', 'Pre-Money', 'Post-Money', 'Total Raised', 'Status', 'Comments'];

const TransactionRow: FC<{ tx: CapIQTransaction; isExpanded: boolean; onToggle: () => void }> = ({
  tx,
  isExpanded,
  onToggle,
}) => {
  const hasExpandableContent = Boolean(tx.secondary_features || tx.comments);

  return (
    <RoundsTableRow
      onClick={hasExpandableContent ? onToggle : undefined}
      sx={{ cursor: hasExpandableContent ? 'pointer' : 'default' }}
    >
      <RoundsTableCell>{tx.round_number ?? '—'}</RoundsTableCell>

      <RoundsTableCell sx={{ textAlign: 'left !important' as 'left', overflow: 'hidden' }}>
        {tx.primary_features ?? '—'}
        {tx.secondary_features && (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              fontSize: '0.7rem',
              ...(isExpanded
                ? { whiteSpace: 'normal', wordBreak: 'break-word' }
                : { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }),
            }}
          >
            {tx.secondary_features}
          </Typography>
        )}
      </RoundsTableCell>

      <RoundsTableCell>{formatDate(tx.announced_date)}</RoundsTableCell>
      <RoundsTableCell>{formatCapIQAmount(tx.premoney_val)}</RoundsTableCell>
      <RoundsTableCell>{formatCapIQAmount(tx.postmoney_val)}</RoundsTableCell>
      <RoundsTableCell>{formatCapIQAmount(tx.total_value)}</RoundsTableCell>
      <RoundsTableCell sx={{ textTransform: 'capitalize' }}>{tx.status ?? '—'}</RoundsTableCell>

      <RoundsTableCell sx={{ textAlign: 'left !important' as 'left', overflow: 'hidden' }}>
        {tx.comments ? (
          <span
            style={isExpanded
              ? { whiteSpace: 'normal', wordBreak: 'break-word' }
              : { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'block' }
            }
          >
            {tx.comments}
          </span>
        ) : '—'}
      </RoundsTableCell>
    </RoundsTableRow>
  );
};

const CapIQFundingRoundsCard: FC<CapIQFundingRoundsCardProps> = ({ companyId }) => {
  const { data, isLoading } = useGetCompanyCapIQFundingRounds(companyId);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const transactions = data?.transactions ?? [];

  const toggle = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  if (isLoading) {
    return <Skeleton variant="rectangular" width="100%" height={160} sx={{ borderRadius: 1 }} />;
  }

  if (transactions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
        No fundraising data available from Capital IQ for this company.
      </Typography>
    );
  }

  return (
    <RoundsTable>
      <colgroup>
        <col style={{ width: '4%' }} />
        <col style={{ width: '18%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '11%' }} />
        <col style={{ width: '11%' }} />
        <col style={{ width: '11%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '25%' }} />
      </colgroup>
      <thead>
        <RoundsTableRow>
          {COLUMN_HEADERS.map(h => <RoundsTableHead key={h}>{h}</RoundsTableHead>)}
        </RoundsTableRow>
      </thead>
      <tbody>
        {transactions.map((tx, i) => {
          const id = tx.transaction_id ?? String(i);
          return (
            <TransactionRow
              key={id}
              tx={tx}
              isExpanded={expanded.has(id)}
              onToggle={() => toggle(id)}
            />
          );
        })}
      </tbody>
    </RoundsTable>
  );
};

export default CapIQFundingRoundsCard;
